// Generated by TaroBase SDK Generator: 2026-05-10T07:46:35.104Z
// Collection: users/$userAddress
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Player profile and cumulative score. User self-creates on first pick; backend awards points when rounds settle.
 */

export interface UsersRequest {
  userAddress: AddressType;
  totalPoints: number | TimeOperation | IncrementOperation | TokenAmount;
  totalWins: number | TimeOperation | IncrementOperation | TokenAmount;
  totalRoundsPlayed: number | TimeOperation | IncrementOperation | TokenAmount;
  lastPlayedAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface UsersResponse {
  userAddress: string;
  totalPoints: number;
  totalWins: number;
  totalRoundsPlayed: number;
  lastPlayedAt: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a Users operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUsers(userAddress: string, data: UsersRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `users/${userAddress}`, document: doc };
}

/**
 * User can self-create their own profile (when @user.address == $userAddress), OR the backend (PROJECT_VAULT_ADDRESS) can create a profile when awarding points to a player who has not yet self-registered. Frontend/backend must initialize totalPoints, totalWins, totalRoundsPlayed, and lastPlayedAt to 0 on first creation. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setUsers(userAddress: string, data: UsersRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`users/${userAddress}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting Users: ${formatError(error)}`);
    return false;
  }
}

export type UsersRequestUpdate = Partial<UsersRequest>;

/**
 * Build a Users update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateUsers(userAddress: string, data: UsersRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `users/${userAddress}`, document: doc };
}

/**
 * Only the backend (PROJECT_VAULT_ADDRESS) updates points/wins/rounds when a round settles. Awards POINTS_PER_WIN to winners and POINTS_PER_PARTICIPATION to others. (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateUsers(userAddress: string, data: UsersRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`users/${userAddress}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating Users: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read user profiles for leaderboards and stats.
   (Get Single Item)
 */
export async function getUsers(userAddress: string): Promise<UsersResponse | null> {
  try {
    const result = await get(`users/${userAddress}`);
    return result as UsersResponse | null;
  } catch (error) {
    console.error(`Error getting Users: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single Users document. (
  Read Operation Details: Anyone can read user profiles for leaderboards and stats.
  )
 */
export function subscribeUsers(callback: (data: UsersResponse | null) => void, userAddress: string): Promise<() => Promise<void>> {
  return subscribe(`users/${userAddress}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as UsersResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to Users at path '${`users/${userAddress}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many Users items from collection users
 
  Read Operation Details: Anyone can read user profiles for leaderboards and stats.
  
 */
export async function getManyUsers(filter: string = ""): Promise<UsersResponse[]> {
  try {
    const results = await get("users", { prompt: filter });
    return Array.isArray(results) ? results as UsersResponse[] : [];
  } catch (error) {
    console.error(`Error getting Users collection at path 'users': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Users collection at users
 
  Read Operation Details: Anyone can read user profiles for leaderboards and stats.
  
 */
export function subscribeManyUsers(callback: (data: UsersResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("users", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as UsersResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Users collection at path '${"users"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all Users items from collection users
 
  Read Operation Details: Anyone can read user profiles for leaderboards and stats.
  
 */
export async function getAllUsers(filter: string = ""): Promise<UsersResponse[]> {
  try {
    const results = await get("users", { prompt: filter });
    return Array.isArray(results) ? results as UsersResponse[] : [];
  } catch (error) {
    console.error(`Error getting Users collection at path 'users': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Users collection at users
 
  Read Operation Details: Anyone can read user profiles for leaderboards and stats.
  
 */
export function subscribeAllUsers(callback: (data: UsersResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("users", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as UsersResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Users collection at path '${"users"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count Users items in collection users.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countUsers(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("users", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting Users at path 'users': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on Users items in collection users.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateUsers(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("users", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating Users at path 'users': ${formatError(error)}`);
    return { value: 0 };
  }
}

